/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const EVENT_TYPE_STYLE = {
  "call.incoming": { bg: "#dbeafe", color: "#1d4ed8", label: "Incoming", icon: "call_received" },
  "call.ended":    { bg: "#dcfce7", color: "#15803d", label: "Ended",    icon: "call_end" },
  "call.recording":{ bg: "#fef9c3", color: "#92400e", label: "Recording",icon: "mic" },
  "call.unknown":  { bg: "#f3f4f6", color: "#6b7280", label: "Unknown",  icon: "help_outline" },
};

const PROVIDER_COLORS = {
  retell:   { bg: "#ede9fe", color: "#6d28d9" },
  pbx: { bg: "#ffedd5", color: "#c2410c" },
};

const EVENT_ORDER = ["call.incoming", "call.recording", "call.ended", "call.unknown"];

const formatTs = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

const WebhookCallDetailsPanel = ({ callId, onClose = () => {} }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pretty");
  const [selectedEventIdx, setSelectedEventIdx] = useState(0);

  const loadEvents = async () => {
    if (!callId) return;
    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_TELEPHONY_EVENTS +
        `?callId=${encodeURIComponent(callId)}&limit=50`;
      const response = await getData(requestUrl);
      if (response.status) {
        // sort oldest first for timeline
        const sorted = [...(response.data || [])].sort(
          (a, b) =>
            new Date(a.normalized?.timestamp || a.createdAt) -
            new Date(b.normalized?.timestamp || b.createdAt)
        );
        setEvents(sorted);
        setSelectedEventIdx(0);
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (callId) {
      setActiveTab("pretty");
      setSelectedEventIdx(0);
      setEvents([]);
      loadEvents();
    }
  }, [callId]);

  // ── derived ────────────────────────────────────────────────────────────────
  const firstEvent = events[0];
  const lastEvent  = events[events.length - 1];
  const phone      = firstEvent?.normalized?.phone || firstEvent?.phone || "";
  const provider   = firstEvent?.provider || "";
  const provStyle  = PROVIDER_COLORS[provider] || { bg: "#f3f4f6", color: "#374151" };

  const durationSec = (() => {
    const endEv = events.find((e) => e.eventType === "call.ended");
    return endEv?.normalized?.duration || null;
  })();

  const selectedEvent = events[selectedEventIdx] || null;

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="webhookCallDetailsPanel"
      aria-labelledby="webhookCallDetailsPanelLabel"
    >
      {/* ── header ── */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary icon-md">call_log</span>
          </div>
          <div>
            <h5
              className="offcanvas-title fw-bold text-white mb-0"
              id="webhookCallDetailsPanelLabel"
            >
              {t("Call Timeline")}
            </h5>
            <p className="text-white opacity-75 fs-xs mb-0 font-monospace">
              {callId || "—"}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={onClose}
        />
      </div>

      {/* ── body ── */}
      <div
        className="offcanvas-body px-4 py-3 d-flex flex-column gap-4"
        style={{ overflowY: "auto" }}
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center py-5 text-muted fs-sm">
            {t("No events found for this call")}
          </div>
        ) : (
          <>
            {/* ── call summary card ── */}
            <div className="border rounded-3 p-3 bg-light">
              <p className="fs-xs fw-bold text-uppercase text-muted mb-2">
                {t("Call Summary")}
              </p>
              <div className="row g-2">
                {[
                  {
                    label: t("Phone"),
                    value: phone,
                    mono: true,
                    icon: "phone",
                  },
                  {
                    label: t("Provider"),
                    value: provider,
                    badge: true,
                    bg: provStyle.bg,
                    color: provStyle.color,
                    icon: "router",
                  },
                  {
                    label: t("Events"),
                    value: `${events.length} event${events.length !== 1 ? "s" : ""}`,
                    icon: "data_usage",
                  },
                  {
                    label: t("Duration"),
                    value: durationSec ? `${durationSec}s` : "—",
                    icon: "timer",
                  },
                  {
                    label: t("Started"),
                    value: formatTs(firstEvent?.normalized?.timestamp || firstEvent?.createdAt),
                    icon: "schedule",
                  },
                  {
                    label: t("Last update"),
                    value: formatTs(lastEvent?.normalized?.timestamp || lastEvent?.createdAt),
                    icon: "update",
                  },
                ].map(({ label, value, mono, badge, bg, color, icon }) => (
                  <div className="col-6" key={label}>
                    <div className="bg-white rounded-2 px-3 py-2 border d-flex align-items-center gap-2">
                      <span
                        className="material-symbols-outlined text-primary flex-shrink-0"
                        style={{ fontSize: "16px" }}
                      >
                        {icon}
                      </span>
                      <div className="min-w-0">
                        <p className="fs-xs text-muted mb-0">{label}</p>
                        {badge ? (
                          <span
                            className="badge rounded-pill fw-semibold text-capitalize"
                            style={{ background: bg, color, fontSize: "11px" }}
                          >
                            {value}
                          </span>
                        ) : (
                          <p
                            className={`fs-sm fw-semibold mb-0 text-truncate ${mono ? "font-monospace" : ""}`}
                            title={value}
                          >
                            {value}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── event timeline ── */}
            <div>
              <p className="fs-xs fw-bold text-uppercase text-muted mb-2">
                {t("Event Timeline")}
              </p>
              <div className="d-flex flex-column" style={{ position: "relative" }}>
                {/* vertical line */}
                <div
                  style={{
                    position: "absolute",
                    left: 19,
                    top: 20,
                    bottom: 20,
                    width: 2,
                    background: "#e5e7eb",
                    zIndex: 0,
                  }}
                />
                {events.map((ev, idx) => {
                  const typeStyle =
                    EVENT_TYPE_STYLE[ev.eventType] || EVENT_TYPE_STYLE["call.unknown"];
                  const isSelected = selectedEventIdx === idx;

                  return (
                    <div
                      key={ev._id || idx}
                      className="d-flex align-items-start gap-3 mb-3"
                      style={{ position: "relative", zIndex: 1 }}
                    >
                      {/* circle icon */}
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 40,
                          height: 40,
                          background: isSelected ? typeStyle.color : typeStyle.bg,
                          color: isSelected ? "#fff" : typeStyle.color,
                          border: `2px solid ${typeStyle.color}`,
                          cursor: "pointer",
                          transition: "background 0.15s",
                        }}
                        onClick={() => setSelectedEventIdx(idx)}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "18px" }}
                        >
                          {typeStyle.icon}
                        </span>
                      </div>

                      {/* content */}
                      <div
                        className={`flex-grow-1 border rounded-3 px-3 py-2 ${isSelected ? "border-primary" : ""}`}
                        style={{
                          background: isSelected
                            ? "rgba(var(--bs-primary-rgb),0.04)"
                            : "#fff",
                          cursor: "pointer",
                          transition: "border-color 0.15s",
                        }}
                        onClick={() => setSelectedEventIdx(idx)}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <span
                            className="badge rounded-pill fw-semibold"
                            style={{
                              background: typeStyle.bg,
                              color: typeStyle.color,
                              fontSize: "11px",
                            }}
                          >
                            {typeStyle.label}
                          </span>
                          <span className="ms-auto fs-xs text-muted">
                            {formatTs(ev.normalized?.timestamp || ev.createdAt)}
                          </span>
                        </div>
                        <div className="d-flex gap-3">
                          {ev.normalized?.duration && (
                            <span className="fs-xs text-muted">
                              {t("Duration")}: <strong>{ev.normalized.duration}s</strong>
                            </span>
                          )}
                          {ev.normalized?.recordingUrl && (
                            <a
                              href={ev.normalized.recordingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="fs-xs text-primary d-flex align-items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontSize: "13px" }}
                              >
                                mic
                              </span>
                              {t("Recording")}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── selected event detail ── */}
            {selectedEvent && (
              <div>
                <p className="fs-xs fw-bold text-uppercase text-muted mb-2">
                  {t("Event Detail")}
                  <span className="badge bg-secondary ms-2 fw-normal text-capitalize">
                    {EVENT_TYPE_STYLE[selectedEvent.eventType]?.label || selectedEvent.eventType}
                  </span>
                </p>

                {/* tab switcher */}
                <ul className="nav nav-tabs border-0 gap-2 mb-0">
                  {[
                    { key: "pretty", label: t("Pretty") },
                    { key: "normalized", label: t("Normalized") },
                    { key: "raw", label: t("Raw JSON") },
                  ].map(({ key, label }) => (
                    <li className="nav-item" key={key}>
                      <button
                        className={`nav-link px-3 py-2 fs-xs fw-semibold border-0 rounded-top ${
                          activeTab === key
                            ? "bg-primary text-white"
                            : "bg-light text-muted"
                        }`}
                        onClick={() => setActiveTab(key)}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* pretty view */}
                {activeTab === "pretty" && (
                  <div className="border rounded-3 rounded-tl-0 p-3 bg-light">
                    <div className="row g-2">
                      {[
                        { label: t("Event Type"),  value: selectedEvent.eventType },
                        { label: t("Call ID"),      value: selectedEvent.callId || selectedEvent.normalized?.callId },
                        { label: t("Phone"),        value: selectedEvent.phone || selectedEvent.normalized?.phone, mono: true },
                        { label: t("Provider"),     value: selectedEvent.provider },
                        { label: t("Duration"),     value: selectedEvent.normalized?.duration ? `${selectedEvent.normalized.duration}s` : null },
                        { label: t("Webhook"),      value: selectedEvent.webhookId?.name || selectedEvent.webhookId?.customid },
                        { label: t("Company"),      value: selectedEvent.company?.name },
                        { label: t("User"),         value: selectedEvent.user ? `${selectedEvent.user.name || ""} ${selectedEvent.user.surname || ""}`.trim() || selectedEvent.user.email : null },
                        { label: t("Timestamp"),    value: formatTs(selectedEvent.normalized?.timestamp) },
                        { label: t("Received at"),  value: formatTs(selectedEvent.createdAt) },
                      ]
                        .filter((item) => item.value)
                        .map(({ label, value, mono }) => (
                          <div className="col-6" key={label}>
                            <div className="bg-white rounded-2 px-3 py-2 border">
                              <p className="fs-xs text-muted mb-0">{label}</p>
                              <p
                                className={`fs-sm fw-semibold mb-0 text-truncate ${mono ? "font-monospace" : ""}`}
                                title={value}
                              >
                                {value}
                              </p>
                            </div>
                          </div>
                        ))}

                      {selectedEvent.normalized?.recordingUrl && (
                        <div className="col-12">
                          <div className="bg-white rounded-2 px-3 py-2 border d-flex align-items-center gap-2">
                            <span className="material-symbols-outlined text-primary icon-sm">mic</span>
                            <a
                              href={selectedEvent.normalized.recordingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="fs-xs text-primary text-truncate"
                            >
                              {selectedEvent.normalized.recordingUrl}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* normalized / raw json views */}
                {(activeTab === "normalized" || activeTab === "raw") && (
                  <div
                    className="rounded-3 rounded-tl-0 border overflow-auto"
                    style={{ background: "#1e1e1e", maxHeight: "320px" }}
                  >
                    <pre
                      className="m-0 p-4 fs-xs"
                      style={{
                        color: "#d4d4d4",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {JSON.stringify(
                        activeTab === "normalized"
                          ? selectedEvent.normalized
                          : selectedEvent.rawPayload,
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WebhookCallDetailsPanel;
